import { useEffect, useState, type FormEvent } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import Container from '@mui/material/Container';
import FormControlLabel from '@mui/material/FormControlLabel';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';

import * as totpApi from '../api/totp';
import { extractErrorMessage } from '../api/errors';
import { useAuth } from '../auth/AuthContext';

/**
 * Two-factor-authentication enrollment page — ROADMAP #18.
 *
 * Three sub-states driven by local state:
 *   1. Loading: we GET /auth/totp/setup, which generates a provisional
 *      secret + otpauth URI. The user is shown the QR + the manual
 *      base32 secret + a "Enter the code from your app" field.
 *   2. After they submit a correct code, we POST /enable and the
 *      server returns ten one-time backup recovery codes. We display
 *      them once with a hard "I've saved them" checkbox guard so the
 *      user can't navigate away without acknowledging.
 *   3. Done: refresh the auth context (so totp_enabled_at is no
 *      longer null) and bounce to /dashboard.
 *
 * Reached from two paths: the RequireAuth gate (forced for
 * superadmin / tenant_admin), and the Profile page "Enable" button
 * (opt-in for any other role).
 */
export function SetupTotp() {
  const navigate = useNavigate();
  const { refresh } = useAuth();

  const [secret, setSecret] = useState<string | null>(null);
  const [uri, setUri] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [code, setCode] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const [backupCodes, setBackupCodes] = useState<string[] | null>(null);
  const [acknowledged, setAcknowledged] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await totpApi.setupTotp();
        if (cancelled) return;
        setSecret(data.base32_secret);
        setUri(data.otpauth_uri);
      } catch (err) {
        if (cancelled) return;
        setLoadError(
          extractErrorMessage(err, 'could not start enrollment'),
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleEnable = async (e: FormEvent) => {
    e.preventDefault();
    if (!secret) return;
    setSubmitError(null);
    setSubmitting(true);
    try {
      const resp = await totpApi.enableTotp(secret, code.trim());
      setBackupCodes(resp.backup_codes);
    } catch (err) {
      setSubmitError(extractErrorMessage(err, 'verification failed'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleFinish = async () => {
    // Refresh the auth context so totp_enabled_at is now non-null —
    // otherwise the RequireAuth gate would bounce the user back here
    // forever.
    await refresh();
    navigate('/dashboard', { replace: true });
  };

  if (loadError) {
    return (
      <Container maxWidth="sm" sx={{ pt: 8 }}>
        <Alert severity="error">{loadError}</Alert>
      </Container>
    );
  }

  if (!secret || !uri) {
    return (
      <Container maxWidth="sm" sx={{ pt: 8 }}>
        <Stack alignItems="center">
          <CircularProgress />
        </Stack>
      </Container>
    );
  }

  // Phase 2: backup codes displayed once.
  if (backupCodes !== null) {
    return (
      <Container maxWidth="sm" sx={{ pt: 6 }}>
        <Stack spacing={3}>
          <Typography variant="h4">Save your recovery codes</Typography>
          <Alert severity="warning">
            These codes are shown <strong>once</strong>. Save them
            somewhere safe — they're the only way back into your
            account if you lose access to your authenticator app.
          </Alert>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack spacing={0.5} component="ul" sx={{ pl: 2, my: 0 }}>
              {backupCodes.map((c) => (
                <Box
                  key={c}
                  component="li"
                  sx={{ fontFamily: 'monospace', fontSize: '1rem' }}
                >
                  {c}
                </Box>
              ))}
            </Stack>
          </Paper>
          <FormControlLabel
            control={
              <Checkbox
                checked={acknowledged}
                onChange={(e) => setAcknowledged(e.target.checked)}
              />
            }
            label="I have saved these recovery codes somewhere safe"
          />
          <Button
            variant="contained"
            disabled={!acknowledged}
            onClick={handleFinish}
          >
            Continue to dashboard
          </Button>
        </Stack>
      </Container>
    );
  }

  // Phase 1: QR + manual secret + 6-digit code field.
  return (
    <Container maxWidth="sm" sx={{ pt: 6 }}>
      <Stack spacing={3}>
        <Typography variant="h4">Set up two-factor authentication</Typography>
        <Typography color="text.secondary">
          Open an authenticator app (Google Authenticator, Authy,
          1Password, etc.), scan the QR code below, then enter the
          6-digit code the app shows. You'll need the code every time
          you sign in.
        </Typography>

        <Card variant="outlined">
          <CardContent>
            <Stack spacing={2} alignItems="center">
              <Box
                sx={{
                  bgcolor: '#fff',
                  p: 2,
                  borderRadius: 1,
                }}
              >
                <QRCodeSVG value={uri} size={200} />
              </Box>
              <Box sx={{ width: '100%' }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                >
                  Can't scan? Enter this secret manually:
                </Typography>
                <Box
                  sx={{
                    fontFamily: 'monospace',
                    p: 1,
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                    wordBreak: 'break-all',
                    fontSize: '0.9rem',
                  }}
                >
                  {secret}
                </Box>
              </Box>
            </Stack>
          </CardContent>
        </Card>

        <Box component="form" onSubmit={handleEnable}>
          <Stack spacing={2}>
            <TextField
              label="6-digit code"
              fullWidth
              required
              value={code}
              onChange={(e) => setCode(e.target.value)}
              inputProps={{ inputMode: 'numeric', maxLength: 8 }}
              disabled={submitting}
            />
            {submitError && <Alert severity="error">{submitError}</Alert>}
            <Button type="submit" variant="contained" disabled={submitting}>
              {submitting ? 'Verifying…' : 'Enable 2FA'}
            </Button>
          </Stack>
        </Box>
      </Stack>
    </Container>
  );
}
